import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import { AccountMenu } from './AccountMenu';
import { AddMenu } from './AddMenu';
import { Avatar } from './Avatar';
import { DeckMark } from './DeckMark';
import { DeckMenu } from './DeckMenu';
import { IconSlot } from './IconSlot';
import { ShareMenu } from './ShareMenu';
import { TitleBlock } from './TitleBlock';
import themes from './HeaderTheme.module.css';
import styles from './HeaderV15.module.css';
import type {
  AccountAction,
  AddItem,
  DeckMenuAction,
  HeaderHover,
  HeaderMenu,
  HeaderTheme,
  ShareScope,
} from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * Must match `--hv15-dur-menu-out`. The menu stays mounted this long after it closes so the exit
 * transition has something to run on — React would otherwise pull the node out on the same frame
 * and the menu would blink out of a fade-in.
 */
const MENU_EXIT_MS = 120;

/**
 * Keeps the closing menu mounted for the length of its exit.
 *
 * Reopening mid-exit cancels it rather than queueing behind it, so a double-click on a trigger
 * lands on a menu that is already coming back rather than one that has to finish leaving first.
 */
function useMenuPresence(menu: HeaderMenu | null) {
  const [rendered, setRendered] = useState<HeaderMenu | null>(menu);
  const [closing, setClosing] = useState(false);
  const renderedRef = useRef(rendered);
  renderedRef.current = rendered;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    if (menu) {
      setRendered(menu);
      setClosing(false);
      return;
    }
    if (renderedRef.current === null) return;
    setClosing(true);
    timer.current = setTimeout(() => {
      setRendered(null);
      setClosing(false);
    }, MENU_EXIT_MS);
    return () => clearTimeout(timer.current);
  }, [menu]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { rendered, closing };
}

/** Which control a story wants painted in its hover state without a real pointer. */
export type HeaderHoverTarget = 'deck-mark' | 'title' | 'agent' | 'directory' | 'canvas' | 'add' | 'present' | 'share' | 'avatar';

export interface HeaderV15Props {
  theme?: HeaderTheme;
  /**
   * How every control answers the pointer. `shape` by default — a chip on the control's own radius
   * plus a tone lift. `tone` drops the chip, `inert` drops both.
   */
  hover?: HeaderHover;
  /** Stories and visual tests only: paints one control's hover state. */
  forceHover?: HeaderHoverTarget | null;

  title: string;
  onRename?: (next: string) => void;
  /** Controlled rename-in-place. Leave undefined and the header manages it (double-click the label). */
  editingTitle?: boolean;
  onEditingTitleChange?: (editing: boolean) => void;
  /**
   * Drawn inside the title button, ahead of the name. The brand kit workspace puts the kit's own
   * mark here, so its title reads `[mark] Acme ⌄` (Guri, 2026-09-03, looking at the kit bar: "move
   * this picker to where it says the kit, so it shows there instead of another one").
   *
   * A slot rather than a `markSrc`, for the same reason `contextual` is one: the header has no
   * business knowing what a kit mark is, and the moment it took a src it would also own the ground
   * colour, the initial fallback and the empty state that `KitMark` already owns. Handed straight
   * to `TitleBlock.leading` — read the note there for why the mark is absent while the name is
   * being typed, and why that is correct rather than a gap to fix.
   *
   * The slot does not size what it is given: the title button is a fixed 24px, so pass something
   * around 18-20px. Anything taller overhangs the bar's rhythm rather than being squashed to fit.
   */
  titleLeading?: React.ReactNode;
  /**
   * Replaces `DeckMenu` under the title caret. Everything else about the menu is unchanged — the
   * same `menuLayer anchorLeft` wrapper, so it inherits the exit transition, the Escape handler and
   * the outside-pointer close for free, and `openMenu`/`defaultOpenMenu` still address it as
   * `'deck'`.
   *
   * A render prop and not a plain node, because the two things a title menu ALWAYS has to do are
   * this component's own internal state and nothing outside can reach them: closing itself, and
   * dropping the name under the caret into the in-place edit field. Those are `setMenu(null)` and
   * `setEditing(true)`, which are internal unless the host happens to control `openMenu` and
   * `editingTitle` — and a host should not have to lift both into its own state just to draw a
   * different list of rows. `DeckMenu`'s `onAction` has done exactly this inline since day one;
   * this hands the same two moves to whoever draws the menu instead.
   *
   * `rename` closes first and then edits, in that order, which is the order `DeckMenu`'s handler
   * has always used. With the header uncontrolled the two land in one React batch and the order is
   * academic — but a host controlling `openMenu` or `editingTitle` can render between them, and
   * "closed, then editing" is the only order that can never paint a menu on top of the field it
   * just opened. Do not reorder it because it looks arbitrary.
   *
   * Omitted, the deck menu is what opens — which is every existing caller, unchanged.
   */
  titleMenu?: (api: { close: () => void; rename: () => void }) => React.ReactNode;
  /**
   * What the title button ANNOUNCES, when its own name is not the whole answer — passed straight to
   * `TitleBlock.menuLabel`. A surface that replaces the deck menu with a menu of somewhere else to
   * go has to say so: the kit workspace passes `Switch brand kit, currently Northwind`.
   */
  titleMenuLabel?: string;
  /** What the in-place rename field is editing. `Deck name` unless a surface says otherwise. */
  nameLabel?: string;

  /** Controlled menu. Leave undefined and the header manages it. */
  openMenu?: HeaderMenu | null;
  onOpenMenuChange?: (menu: HeaderMenu | null) => void;
  /** Uncontrolled starting menu — the only way a static story can show S4-S7. */
  defaultOpenMenu?: HeaderMenu | null;

  /** The agent panel is collapsed or expanded. */
  agentOn?: boolean;
  onAgentToggle?: () => void;
  /**
   * The DIRECTORY panel is collapsed or expanded. Supply the handler and the toggle appears after
   * the view switch — chat, canvas, sidebar (Guri, 2026-08-30, reading his own bar aloud). Omit it
   * and the bar is exactly the pre-merge bar, which is what every existing story renders.
   */
  directoryOn?: boolean;
  onDirectoryToggle?: () => void;
  /** The deck is on the infinite canvas. A mode, not a panel. */
  canvasOn?: boolean;
  onCanvasToggle?: () => void;

  /**
   * The merged header's contextual run, laid after the panel cluster: whatever the open surfaces
   * put on the bar — the directory's kind switcher, the previewed item's name and its controls.
   * A slot rather than knowledge of any of them, for the same reason `PanelHeader.trailing` is
   * one: which controls exist is the host's problem, and a bar that knew would hard-code a
   * trigger for whichever is not built yet.
   */
  contextual?: React.ReactNode;
  /** Extra right-cluster controls, drawn between Share and the avatar — the workspace's Design slot. */
  trailing?: React.ReactNode;

  onDeckMarkClick?: () => void;
  onDeckMenuAction?: (action: DeckMenuAction) => void;
  onAdd?: (item: AddItem) => void;
  /**
   * Drop the right cluster's `+`. The merged header's column-aligned pass moved add onto the
   * DIRECTORY segment (Beka's Frame 7 draws it there, at the segment's seam end), and two plus
   * glyphs on one bar is a coin toss. Off by default so every existing surface keeps its add.
   */
  hideAdd?: boolean;
  /**
   * Same deal for Present and Share: the /deck workspace moved both onto the preview column's own
   * header line (Guri, 2026-08-30 — "those three buttons move to the inside of the preview"), and
   * doubled controls are a coin toss. Off by default; every other surface keeps its cluster.
   */
  hidePresent?: boolean;
  hideShare?: boolean;
  /**
   * Drop the canvas/layout view switch. A kit has one view, so on `/brand-kits/:kitId` the switch
   * was a button that did nothing at all (Guri, 2026-09-03: "remove the icon... keep the chat icon,
   * and the rest is fine"); the /deck workspace passes it too, for the opposite reason — it HAS a
   * canvas, and the switch was a door to the other editor wearing a view toggle's clothes. Off by
   * default all the same: a host that means it says so, and `/canvas` itself still draws it.
   *
   * Deliberately NOT derived from `onCanvasToggle` being absent, tempting as that is: three static
   * stories render `canvasOn` with no handler to photograph the switch's own two states, and
   * inferring it would silently delete the control those stories exist to show. Same shape as
   * `hideAdd`/`hidePresent`/`hideShare` — the host says so out loud.
   */
  hideCanvas?: boolean;
  /**
   * ⌘K: hand the job to the agent instead of adding something yourself.
   *
   * Its own prop rather than an `AddItem`, which is what it used to be. The add menu's row went
   * (2026-09-05) because asking the agent is not a thing you *add* — but the SHORTCUT is a real
   * affordance the chat pane is built around, and routing it through `onAdd` meant deleting the row
   * silently deleted the only ⌘K binding on the deck. Separate concerns, separate props: the menu
   * lists what the deck can author, this focuses the composer.
   */
  onAskAgent?: () => void;
  onPresent?: () => void;

  /** Who the deck is visible to. The share menu's first row names it and changes it. */
  shareScope?: ShareScope;
  /** The share link itself, printed as the share menu's second row. */
  shareUrl?: string;
  /** Called with the scope the visibility row was pressed to reach. */
  onShareScopeChange?: (scope: ShareScope) => void;
  onCopyLink?: () => void;
  linkCopied?: boolean;

  userName?: string;
  avatarUrl?: string;
  credits?: { used: number; total: number; topup?: number; loading?: boolean };
  onAccountAction?: (action: AccountAction) => void;

  /**
   * Bind the header's global shortcuts: ⌘⌥A toggles the agent panel. Off by default so a Storybook
   * page full of headers does not fight over the keyboard; a real route turns it on once.
   *
   * ⌘1..⌘4 are not bound, and must not be: the browser owns ⌘1..⌘9 for switching tabs. Bound here they
   * added an item instead, so someone switching tabs piled up empty artifacts and never left the page.
   *
   * ⌘K is deliberately not among them any more: it used to hand the job to the agent, which is the
   * agent panel's press and not the header's, and leaving the app's most-reached-for shortcut bound
   * here would keep it out of everyone else's hands.
   */
  shortcuts?: boolean;

  className?: string;
}

/**
 * Deck's application header, v1.5.
 *
 * ```
 * left    logo · title ⌄ · agent · canvas
 * right   + · present · share · avatar
 * ```
 *
 * Nothing else belongs here — anything acting on one panel or one item lives on that panel. The
 * bar is 40px with symmetric 8px ends; controls are 30px slots that never paint a background, in
 * any state, so an active control is the `-fill` twin of its glyph at the same tone.
 *
 * Menus anchor to their trigger, never the window: the deck menu's left edge on the title's left
 * edge; the add and account menus' right edge on the avatar slot's; the share menu's on the share
 * slot's.
 *
 * Every state prop is optional and falls back to internal state, so this renders standalone in a
 * story and drives from a route with the same component.
 */
export function HeaderV15({
  theme: themeProp,
  hover = 'shape',
  forceHover = null,
  title,
  onRename,
  editingTitle,
  onEditingTitleChange,
  titleLeading,
  titleMenu,
  titleMenuLabel,
  nameLabel,
  openMenu,
  onOpenMenuChange,
  defaultOpenMenu = null,
  agentOn = false,
  onAgentToggle,
  directoryOn = false,
  onDirectoryToggle,
  canvasOn = false,
  onCanvasToggle,
  contextual,
  trailing,
  onDeckMarkClick,
  onDeckMenuAction,
  onAdd,
  hideAdd = false,
  hidePresent = false,
  hideShare = false,
  hideCanvas = false,
  onAskAgent,
  onPresent,
  shareScope = 'private',
  shareUrl,
  onShareScopeChange,
  onCopyLink,
  linkCopied = false,
  userName = 'User',
  avatarUrl,
  credits,
  onAccountAction,
  shortcuts = false,
  className,
}: HeaderV15Props) {
  const theme = useV15Theme(themeProp);
  const [internalMenu, setInternalMenu] = useState<HeaderMenu | null>(defaultOpenMenu);
  const [internalEditing, setInternalEditing] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const menu = openMenu !== undefined ? openMenu : internalMenu;
  const editing = editingTitle !== undefined ? editingTitle : internalEditing;

  const setMenu = useCallback(
    (next: HeaderMenu | null) => {
      if (openMenu === undefined) setInternalMenu(next);
      onOpenMenuChange?.(next);
    },
    [openMenu, onOpenMenuChange],
  );

  const setEditing = useCallback(
    (next: boolean) => {
      if (editingTitle === undefined) setInternalEditing(next);
      onEditingTitleChange?.(next);
    },
    [editingTitle, onEditingTitleChange],
  );

  const toggleMenu = (which: HeaderMenu) => setMenu(menu === which ? null : which);

  // `menu` is the truth for what is OPEN; `rendered` is what is still on screen, which during an
  // exit is one menu longer. Triggers read `menu` so a control drops its active glyph the instant
  // it is clicked — the click should register immediately even while the menu is still leaving.
  const { rendered, closing } = useMenuPresence(menu);

  // Escape closes whatever is open, and an outside pointer does the same — a menu that outlives
  // the click that dismissed it is the single most reported bug shape in this codebase's popovers.
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    /*
     * Outside the MENU, not outside the bar.
     *
     * The guard used to be `barRef.contains(target)`, which meant a press anywhere in the header —
     * the title, the logo, the empty run between clusters — left the menu standing. The bar is not
     * the popover; only the popover is.
     *
     * Two exemptions, and both are necessary rather than tidy. A press inside the open layer is the
     * user using the menu. A press on a menu TRIGGER is left to that trigger's own click: closing
     * here first would make `toggleMenu` see a closed menu a moment later and re-open the very one
     * that was just dismissed, so the same button could never close what it opened.
     */
    const onDown = (e: PointerEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el?.closest('[data-menu-layer]')) return;
      if (el?.closest('[aria-haspopup="menu"]')) return;
      setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menu, setMenu]);

  // ⌘⌥A and ⌘K. Kept out of the menu components so the shortcuts work with every menu closed, which
  // is the only time anyone actually presses them.
  useEffect(() => {
    if (!shortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.altKey && (e.key === 'a' || e.key === 'A' || e.code === 'KeyA')) {
        e.preventDefault();
        onAgentToggle?.();
        return;
      }
      if (e.altKey) return;
      // ⌘K asks the agent rather than naming a thing to make, which is why it has its own prop.
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        onAskAgent?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcuts, onAgentToggle, onAskAgent]);

  // ⌘C copies the share link, and ONLY while the share menu is open. Bound globally it would take
  // the universal copy shortcut away from every text selection in the app, which is why it is not
  // in the block above with ⌘1..⌘4 — the menu being open is the whole scope of it.
  useEffect(() => {
    // No scope guard. The copy row is live at every audience — the link is the deck's address, not
    // a thing publishing conjures — and the row prints `⌘C` at every audience too, so a shortcut
    // that quietly did nothing on `Only me` would make that hint a lie.
    if (!shortcuts || menu !== 'share') return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        onCopyLink?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcuts, menu, onCopyLink]);

  const hoverProps = (target: HeaderHoverTarget) => ({
    theme,
    hover,
    forceHover: forceHover === target,
  });

  return (
    <div ref={barRef} className={cn(themes[theme], styles.bar, className)}>
      <div className={styles.left}>
        <DeckMark onClick={onDeckMarkClick} {...hoverProps('deck-mark')} />

        <div className={styles.titleAnchor}>
          <TitleBlock
            title={title}
            leading={titleLeading}
            editing={editing}
            onEditingChange={setEditing}
            onRename={onRename}
            onCaretClick={() => toggleMenu('deck')}
            // `menu`, not `rendered`: the same reason every trigger on this bar reads it. A menu on
            // its way out is already closed as far as the thing that opened it is concerned.
            expanded={menu === 'deck'}
            menuLabel={titleMenuLabel}
            nameLabel={nameLabel}
            {...hoverProps('title')}
          />
          {rendered === 'deck' && !editing ? (
            <div data-menu-layer className={cn(styles.menuLayer, styles.anchorLeft)} data-closing={closing}>
              {titleMenu ? (
                /* The host's menu in the deck menu's exact place — same wrapper, same anchor, same
                   `data-closing`, so a replacement menu cannot end up with a different exit or a
                   different left edge than the one it replaced. All it is handed is the pair of
                   moves that live in this component's state; everything else its rows do is its
                   own business and does not come back through here. */
                titleMenu({
                  close: () => setMenu(null),
                  rename: () => {
                    setMenu(null);
                    setEditing(true);
                  },
                })
              ) : (
                <DeckMenu
                  theme={theme}
                  onAction={(action) => {
                    setMenu(null);
                    // Rename is the deck menu's route into the same in-place field the label's
                    // double-click opens — there is no separate rename dialog.
                    if (action === 'rename') setEditing(true);
                    else onDeckMenuAction?.(action);
                  }}
                />
              )}
            </div>
          ) : null}
        </div>

        <div className={styles.cluster}>
          <IconSlot
            icon="chat-1"
            activeIcon="chat-1-fill"
            active={agentOn}
            // Back to the shared 18px. The old `agent` glyph was drawn on a 20-unit viewBox and
            // needed 20px to match its neighbours' optical weight; `chat-1` is a 24-unit glyph like
            // everything else on the bar, so the compensation would now make it the odd one out.
            label="Agent panel"
            // The key is only bound when `shortcuts` is on (see the effect above), and a surface
            // that has not bound it must not advertise it. The brand kit workspace is exactly that
            // surface: it wires the toggle and not the keyboard.
            title={shortcuts ? 'Agent panel (⌘⌥A)' : 'Agent panel'}
            onClick={onAgentToggle}
            {...hoverProps('agent')}
          />
          {/*
           * The view switch, and the one control on the bar that is NOT an on/off toggle: there is
           * no "off" for a view, only the other one. So it does not take the `-fill` twin its
           * neighbours use to say "on" — the glyph itself carries the whole state.
           *
           * The glyph names the view the press GIVES you, not the one you are in. An icon-only
           * control that showed the current view would be a status readout with nothing about it
           * saying it can be pressed, and you would have to already know where you were to read it.
           * Naming the destination makes glyph and label the same sentence: on the canvas it offers
           * the layout, in the layout it offers the canvas.
           *
           * `pressed` is passed by hand for the same reason. `IconSlot` derives `aria-pressed` from
           * `activeIcon` + `active`, and there is no `activeIcon` here — without this the switch
           * would announce as a plain button and the view state would reach a screen reader through
           * the label alone, one word deep.
           *
           * `hideCanvas` drops it entirely, and two surfaces ask for that. `/brand-kits/:kitId`
           * has only one view, so this was a control with nothing behind it. The /deck workspace
           * has two editors rather than two views, so the press was a NAVIGATION dressed as a
           * toggle — see `useDeckHeader` for why that door is worth closing. Either way, see the
           * prop for why the host has to say so rather than the header reading it off a missing
           * `onCanvasToggle`.
           */}
          {hideCanvas ? null : (
            <IconSlot
              icon={canvasOn ? 'layout-view' : 'canvas-view'}
              pressed={canvasOn}
              label={canvasOn ? 'Layout view' : 'Canvas view'}
              onClick={onCanvasToggle}
              {...hoverProps('canvas')}
            />
          )}
          {/* Chat · canvas · sidebar — Guri's reading of the cluster (2026-08-30). Rendered only
              when the host wires it, so every pre-merge story still draws the two-control cluster
              it was written against; the /deck workspace also unwires it while the agent column
              is open, because the toggle then lives on the column itself. */}
          {onDirectoryToggle ? (
            <IconSlot
              icon="side-bar"
              activeIcon="side-bar-fill"
              active={directoryOn}
              label="Directory panel"
              onClick={onDirectoryToggle}
              {...hoverProps('directory')}
            />
          ) : null}
        </div>

        {/* The contextual run. `min-width: 0` in its class is what lets an artifact name in here
            ellipsise instead of shoving the right cluster off the bar — the same discipline the
            leading run of every 36px band carries. */}
        {contextual ? <div className={styles.contextual}>{contextual}</div> : null}
      </div>

      <div className={styles.right}>
        <div className={cn(styles.cluster, styles.rightIcons)}>
          {hideAdd ? null : (
            <IconSlot
              icon="add"
              activeIcon="add-fill"
              active={menu === 'add'}
              label="Add"
              onClick={() => toggleMenu('add')}
              {...hoverProps('add')}
            />
          )}
          {hidePresent ? null : (
            <IconSlot icon="play" label="Present" onClick={onPresent} {...hoverProps('present')} />
          )}
          {hideShare ? null : (
            <IconSlot
              icon="share-2"
              activeIcon="share-2-fill"
              active={menu === 'share'}
              label="Share"
              onClick={() => toggleMenu('share')}
              {...hoverProps('share')}
            />
          )}

          {rendered === 'share' ? (
            <div data-menu-layer className={cn(styles.menuLayer, styles.anchorRight)} data-closing={closing}>
              <ShareMenu
                theme={theme}
                scope={shareScope}
                url={shareUrl}
                onScopeChange={onShareScopeChange}
                onCopyLink={onCopyLink}
                copied={linkCopied}
              />
            </div>
          ) : null}

          {/* The host's own controls, inside the cluster so they share its rhythm: add, play,
              share, then whatever the workspace adds — Design, today. Any menu they open is their
              own anchor's problem, exactly as the item `⋯` works on the panel bands. */}
          {trailing}
        </div>

        <Avatar
          src={avatarUrl}
          name={userName}
          onClick={() => toggleMenu('account')}
          {...hoverProps('avatar')}
        />

        {rendered === 'add' ? (
          <div data-menu-layer className={cn(styles.menuLayer, styles.anchorRight)} data-closing={closing}>
            <AddMenu
              theme={theme}
              onAdd={(item) => {
                setMenu(null);
                onAdd?.(item);
              }}
            />
          </div>
        ) : null}

        {rendered === 'account' ? (
          <div data-menu-layer className={cn(styles.menuLayer, styles.anchorRight)} data-closing={closing}>
            <AccountMenu
              theme={theme}
              name={userName}
              avatarUrl={avatarUrl}
              credits={credits}
              onAction={(action) => {
                setMenu(null);
                onAccountAction?.(action);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
